import React, { useState, Fragment } from 'react';
import { Button, Typography, Table, Flex, Card } from 'antd';
import { DesktopOutlined, MobileOutlined } from '@ant-design/icons';
import SearchBarV2 from '../../../components/SearchBarV2';
import { CheckListOptions } from '../../../components/PageElementsLib';
import { DeletePageButton } from '../../../components/PagesActionButtons';
import SeoScoreComponent from '../../../components/pages/SeoScoreComponent';
import { useEvent } from '../../events/hooks/useEvent';
import ExportButton from '../../../components/ExportButton';
import ButtonWithRole from '../../../components/ButtonWithRole';
import USER_ROLES from '../../auth/userRoles';
import { CONTENT_WRITE_REFUSED_ROLES } from '../../auth/roleCapabilities';
import ExportCachedPagesModal from './ExportCachedPagesModal';
import CachedPagesActionsModal from './CachedPagesActionsModal';
import { useCharbeePlanId, useUserRole } from '../../../hooks/usePrerenderUser';
import PrevNextPagination from '../../../components/PrevNextPagination';
import { PointInTime, POINT_IN_TIME_COLUMN_WIDTH } from '../../../components/PointInTime';
import { TOOLTIP_ROOT_ID } from '../../../components/Tooltip';
import useFeatureFlag, { FeatureFlag } from '../../../hooks/useFeatureFlag';
import { SeoScoreModal } from '../../seoScoreModal/SeoScoreModal';
import { DomainFilter } from '../../domain-manager/filter';
import { SourceTableCell, SourceLabels } from '../Source';
import { CacheStateCell, CACHE_STATE_FILTER_OPTIONS, recacheButtonType } from './CacheStateCell';

const { Column } = Table;
const { Link } = Typography;

const FIRST_SEEN_TOOLTIP_LABEL = 'Page First Rendered At';

// FIXME: no exact token — any narrower and the "Contains" select leaves the URL
// field too small to type in, so the search wraps to its own row instead.
const SEARCH_BAR_MIN_WIDTH = 320;

const SOURCE_FILTERS_OPTIONS = Object.entries(SourceLabels).map(([source, { label }]) => ({
  text: label,
  value: source,
}));

const CacheManagerTable = ({
  onDomainSelected,
  selectedDomain,
  onSearchConditionChanged,
  onSearch,
  currentSearchQuery,
  currentQCondition,
  onReloadData,
  onClearFilters,
  loading,
  cachedPages,
  onSortAndFilter,
  filteredInfo,
  onRequestRecache,
  onDeleteURLs,
  pagination,
  onAuditURL,
  seoAuditResult,
}) => {
  const [selectedURLs, setSelectedURLs] = useState([]);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [actionsModalVisible, setActionsModalVisible] = useState(false);
  const canUseSeoAudit = useFeatureFlag([FeatureFlag.SeoAudit]);

  const [showSeoScoreModal, setShowSeoScoreModal] = useState(false);
  const [cachedUrl, setCachedUrl] = useState();
  const { track } = useEvent();
  const chargebeePlanId = useCharbeePlanId();
  const userRole = useUserRole();

  const handleTableChange = (_pagination, filters, sorter, details) => {
    onSortAndFilter({
      sortField: sorter.field,
      sortDirection: sorter.order === 'ascend' ? 'ASC' : 'DESC',
      filters,
    });

    const activeFiltersNames = Object.keys(filters).filter((key) => filters[key]?.length > 0);

    if (details.action === 'filter' && activeFiltersNames.length !== 0) {
      track(`Cache URL List Filtered: ${activeFiltersNames}`, {
        subscription_plan: chargebeePlanId,
        filters,
      });
    }
  };

  const deleteURLs = async (urls) => {
    const wasSuccess = await onDeleteURLs(urls);
    if (wasSuccess) {
      setSelectedURLs([]);
      setActionsModalVisible(false);
    }
  };

  const recacheURLs = async (urls) => {
    const wasSuccess = await onRequestRecache(urls);
    if (wasSuccess) {
      setSelectedURLs([]);
      setActionsModalVisible(false);
    }
  };

  const onCheckListOptionsAction = (action) => {
    if (action === 'deleteUrls') {
      deleteURLs(selectedURLs);
    } else if (action === 'requestRecache') {
      recacheURLs(selectedURLs);
    } else if (action === 'openEditUrlsModal') {
      setActionsModalVisible(true);
    }
  };

  const onSeoScoreClick = (url) => {
    if (url.seo_score) {
      setCachedUrl(url);
      setShowSeoScoreModal(true);
      track('SEO Score Clicked', { subscription_plan: chargebeePlanId });
    }
  };

  const rowSelection = React.useMemo(() => {
    if (CONTENT_WRITE_REFUSED_ROLES.includes(userRole)) return false;

    return {
      selectedRowKeys: selectedURLs.map((u) => u.id),
      type: 'checkbox',
      onChange: (_selectedRowKeys, selectedRows) => setSelectedURLs(selectedRows),
      getCheckboxProps: (_record) => ({
        disabled: CONTENT_WRITE_REFUSED_ROLES.includes(userRole),
      }),
    };
  }, [userRole, selectedURLs]);

  const cachedToolTip = React.useCallback(
    (adaptive_type) => (
      <div
        data-tooltip-id={TOOLTIP_ROOT_ID}
        data-tooltip-content={`Rendering was optimized for [${
          adaptive_type === 'mobile' ? 'Mobile' : 'Desktop'
        }] crawlers`}
        data-tooltip-place="top"
      >
        {adaptive_type === 'mobile' ? <MobileOutlined /> : <DesktopOutlined />}
      </div>
    ),
    []
  );

  const cachedSeoScore = React.useCallback(
    (seo_score, record) => (
      <Button type="ghost" size="small" onClick={() => onSeoScoreClick(record)}>
        <SeoScoreComponent score={seo_score} />
      </Button>
    ),
    []
  );

  return (
    <Fragment>
      <ExportCachedPagesModal visible={exportModalVisible} onCancel={() => setExportModalVisible(false)} />
      <CachedPagesActionsModal
        open={actionsModalVisible}
        urls={selectedURLs}
        onCancel={() => setActionsModalVisible(false)}
        onDelete={deleteURLs}
        onRecache={recacheURLs}
      />
      <SeoScoreModal open={showSeoScoreModal} onClose={() => setShowSeoScoreModal(false)} url={cachedUrl} />

      {/* Cached pages table */}
      <Card
        size="small"
        styles={{ body: { padding: 0 }, header: { padding: 10 } }}
        style={{ padding: 0 }}
        title={
          <Flex align="center" gap="small" wrap>
            <DomainFilter domain={selectedDomain} onDomainChange={onDomainSelected} />
            <Flex flex={`1 1 ${SEARCH_BAR_MIN_WIDTH}px`}>
              <SearchBarV2
                initialValue={currentSearchQuery}
                qCondition={currentQCondition}
                onTypingEnd={onSearch}
                onSearchConditionChanged={onSearchConditionChanged}
                disabled={selectedURLs.length > 0}
                placeholder="Please enter at least 3 characters to start your search"
              />
            </Flex>
            <div>
              <Button className="ml-2" loading={loading} onClick={onReloadData} size="small">
                <i className="fe fe-rotate-cw"></i>
              </Button>
              <Button className=" ml-2" loading={loading} onClick={onClearFilters} size="small">
                Clear filters
              </Button>
              <ExportButton hasSelectedUrls={selectedURLs.length > 0} onClick={() => setExportModalVisible(true)} />
            </div>
          </Flex>
        }
      >
        <Table
          rowSelection={rowSelection}
          rowKey="id"
          loading={loading}
          dataSource={cachedPages}
          onChange={handleTableChange}
          pagination={false}
          scroll={{ x: '1900px' }}
          size="small"
          sticky={{ offsetHeader: 0, offsetScroll: 10 }}
        >
          <Column
            title="Last Cached"
            dataIndex="last_refresh"
            key="last_refresh"
            align="center"
            width={POINT_IN_TIME_COLUMN_WIDTH}
            sorter="true"
            defaultSortOrder="descend"
            render={(last_refresh) => <PointInTime value={last_refresh} />}
          />
          <Column
            title="URL"
            dataIndex="url"
            key="url"
            width="500px"
            render={(url) => (
              <Link href={url} target="_blank" rel="noreferrer">
                {url}
              </Link>
            )}
          />
          <Column
            title="Device"
            dataIndex="adaptive_type"
            key="adaptive_type"
            align="center"
            width="74px"
            filteredValue={filteredInfo.adaptive_type || null}
            filters={[
              {
                text: 'Desktop',
                value: 'desktop',
              },
              {
                text: 'Mobile',
                value: 'mobile',
              },
            ]}
            render={cachedToolTip}
          />
          <Column
            title="SEO Score"
            dataIndex="seo_score"
            key="seo_score"
            align="center"
            width="100px"
            sorter="true"
            filteredValue={filteredInfo.seo_score || null}
            render={cachedSeoScore}
          />
          <Column
            title="State"
            dataIndex="cache_state"
            key="cache_state"
            align="center"
            width="100px"
            sorter="true"
            filteredValue={filteredInfo.cache_state || null}
            filters={CACHE_STATE_FILTER_OPTIONS}
            render={(cache_state, record) => (
              <CacheStateCell
                cacheStateName={cache_state}
                recacheAttempts={record.recache_attempts}
                lastRecacheError={record.last_recache_error}
                lastRecacheErrorAt={record.last_recache_error_at}
              />
            )}
          />
          <Column
            title="First Seen"
            dataIndex="created_at"
            key="created_at"
            align="center"
            width={POINT_IN_TIME_COLUMN_WIDTH}
            sorter="true"
            render={(created_at) => <PointInTime value={created_at} label={FIRST_SEEN_TOOLTIP_LABEL} />}
          />
          <Column
            title="Source"
            dataIndex="source"
            key="source"
            align="center"
            width="90px"
            sorter="true"
            filteredValue={filteredInfo.source || null}
            filters={SOURCE_FILTERS_OPTIONS}
            render={(source) => <SourceTableCell sourceName={source} />}
          />
          <Column
            title="Last crawled at"
            dataIndex="last_delivery_at"
            key="last_delivery_at"
            align="center"
            width={POINT_IN_TIME_COLUMN_WIDTH}
            sorter="true"
            render={(last_delivery_at) => <PointInTime value={last_delivery_at} />}
          />
          <Column
            title=""
            key="action"
            align="right"
            width="150px"
            render={(_, record) => (
              <Flex align="center">
                <ButtonWithRole
                  disabledFor={CONTENT_WRITE_REFUSED_ROLES}
                  size="small"
                  className="ml-2"
                  type={recacheButtonType(record.cache_state)}
                  onClick={() => onRequestRecache([record])}
                >
                  Recache
                </ButtonWithRole>
                {canUseSeoAudit && (
                  <ButtonWithRole
                    disabledFor={[USER_ROLES.GUEST]}
                    size="small"
                    className="ml-2"
                    onClick={() => onAuditURL(record.url)}
                    disabled={seoAuditResult.inProgress}
                  >
                    Audit
                  </ButtonWithRole>
                )}

                <DeletePageButton
                  onClick={() => onDeleteURLs([record])}
                  tooltip="Delete from Prerender Cache"
                />
              </Flex>
            )}
          />
        </Table>
        <PrevNextPagination {...pagination} />
      </Card>
      <CheckListOptions
        count={selectedURLs.length}
        name="URL"
        actions={[
          { name: 'requestRecache', text: 'Recache' },
          { name: 'deleteUrls', icon: 'trash-2' },
          { name: 'openEditUrlsModal', icon: 'more-vertical' },
        ]}
        onsubmit={(action) => onCheckListOptionsAction(action)}
        isActive={!loading}
        onclose={() => setSelectedURLs([])}
      />
    </Fragment>
  );
};

export default CacheManagerTable;
